/**
 * Module dependencies.
 */

import { Navigate, Route, Routes } from 'react-router-dom';
import { Home } from 'app/components/pages/home/home';
import { appRoutes } from 'app/routes';
import React, { useMemo } from 'react';
import { DashboardHeader } from './components/templates/dashboard/dashboard-header';
import { SkinDashboard } from './components/templates/dashboard/skin-dashboard';
import { DashboardOverview } from './components/pages/dashboard/overview/dashboard-overview';
import { SkinSettings } from './components/templates/dashboard/skin-settings';
import { SkinDeveloper } from './components/templates/dashboard/skin-developer';
import { SettingsOverview } from './components/pages/settings/overview/settings-overview';
import { SettingsProfile } from './components/pages/settings/profile/settings-profile';
import { DeveloperOverview } from './components/pages/developer/overview/developer-overview';
import { SmsTest } from './components/pages/developer/sms-test';
import { createLogsTemplate } from './components/pages/developer/logs/form-config';
import { createLogsTherapyTemplate } from './components/pages/developer/logs-therapy/form-config';
import { createIgnoreUrlLogsTemplate } from './components/pages/developer/ignore-url-logs/form-config';
import { createTranslationsTemplate } from './components/pages/developer/translations/form-config';
import { createMissingTranslationsTemplate } from './components/pages/developer/missing-translations/form-config';
import { useTranslation } from 'react-i18next';
import { SkinUsers } from './components/templates/dashboard/skin-users';
import { UsersOverview } from './components/pages/users/overview/users-overview';
import { createUsersTemplate } from './components/pages/users/users/form-config';
import { listCrudRoutes } from './components/organisms/crud/utils';
import { UsersDetailOverview } from './components/pages/users/user-detail-overview/user-detail-overview';
import { BiometricData } from './components/pages/users/biometric-data/biometric-data';
import { UsersMetrics } from './components/pages/users/users-metrics/users-metrics';
import { createUserSettingsTemplate } from './components/pages/users/settings/form-config';
import { createUserAuthorizationsTemplate } from './components/pages/users/authorizations/form-config';
import { createUserPushTokensTemplate } from './components/pages/users/push-tokens/form-config';
import { createUserPinNumbersTemplate } from './components/pages/users/pin-numbers/form-config';
import { MedicalInstrumentsOverview } from './components/pages/medical-instruments/overview/medical-instruments-overview';
import { SkinMedicalInstruments } from './components/templates/dashboard/skin-medical-instruments';
import { createDrugsTemplate } from './components/pages/medical-instruments/drugs/form-config';
import { createLanguagesTemplate } from './components/pages/settings/languages/form-config';
import { createConfigTemplate } from './components/pages/settings/config/form-config';
import { SkinTables } from './components/templates/dashboard/skin-tables';
import { TablesOverview } from './components/pages/tables/overview/tables-overview';
import { createDialCodesTemplate } from './components/pages/tables/dial-codes/form-config';
import { createDevicesTemplate } from './components/pages/tables/devices/form-config';
import { createMissingDrugsTemplate } from './components/pages/medical-instruments/missing-drugs/form-config';
import { ConvertMissingDrug } from './components/pages/medical-instruments/missing-drugs/convert-missing-drug';
import { MissingDrugDetail } from './components/pages/medical-instruments/missing-drugs/missing-drug-detail';
import { SkinSupport } from './components/templates/dashboard/skin-support';
import { SupportOverview } from './components/pages/support/overview/support-overview';
import { createSupportOpenTicketsTemplate } from './components/pages/support/open-tickets/form-config';
import { TicketDetail } from './components/pages/support/ticket-detail/ticket-detail';
import { createUserDefinedAlarmsTemplate } from './components/pages/users/defined-alarms/form-config';
import { createUserDevicesTemplate } from './components/pages/users/devices/form-config';
import { createUserMedicationsTemplate } from './components/pages/users/medications/form-config';
import { createExtendedAlarmsTemplate } from './components/pages/users/medications/form-alarms-config';
import { createExtendedDeviceAlarmsTemplate } from './components/pages/users/medications/form-alarms-device-config';
import { createUserAlarmsSyncConfig } from './components/pages/users/dev-tools/form-config';
import { SalesList } from './components/pages/users/sales/sale-list';
import { SaleCreate } from './components/pages/users/sales/sale-create';
import { SaleEdit } from './components/pages/users/sales/sale-edit';
import { SaleDetail } from './components/pages/users/sales/sale-detail';
import { createSubscriptionsTemplate } from './components/pages/services/subscriptions/form-config';
import { SkinServices } from './components/templates/dashboard/skin-services';
import { createSubscriptionsPharmaciesTemplate } from './components/pages/services/pharmacies/form-config';
import { createPharmaciesConsentsTemplate } from './components/pages/services/pharmacies/consents/form-config';
import { createCCIsTemplate } from './components/pages/medical-instruments/ccis/form-config';
import { createSubscriptionsDetailPharmaciesTemplate } from './components/pages/services/subscriptions/pharmacies/form-config';
import { createSubscriptionsDetailUsersTemplate } from './components/pages/services/subscriptions/users/form-config';
import { createSubscriptionsDetailPharmaciesConsentsTemplate } from './components/pages/services/subscriptions/pharmacies/consents/form-config';
import { createSubscriptionsDetailPharmaciesIntegrationsTemplate } from './components/pages/services/subscriptions/pharmacies/integrations/form-config';
import { createPharmaciesIntegrationsTemplate } from './components/pages/services/pharmacies/integrations/form-config';
import { createCCIsCodesTemplate } from './components/pages/medical-instruments/ccis-codes/form-config';
import { createCCIsDrugsTemplate } from './components/pages/medical-instruments/ccis-drugs/form-config';
import { createOrdersTemplate } from './components/pages/services/orders/form-config';
import { OrderDetail } from './components/pages/services/orders/order-detail';
import { createSalesTemplate } from './components/pages/services/sales/form-config';
import { createPharmaciesSharedAccessesTemplate } from './components/pages/services/pharmacies/shared-accesses/form-config';
import { createPharmacyPlanTypesTemplate } from './components/pages/services/pharmacy-plans/form-config';
import { PharmacyPlan } from './components/pages/services/pharmacies/plans/pharmacy-plan';
import { createPharmaciesUserStatsTemplate } from './components/pages/services/pharmacies/user-stats/form-config';
import { createPharmacyLedgerTemplate } from './components/pages/services/pharmacies/ledger/form-config';
import { createPharmacyCampaignsTemplate } from './components/pages/services/pharmacies/campaigns/form-config';

/**
 * Export `AppRoutes` component.
 */

export function AppRoutes(): JSX.Element {
  const [translate] = useTranslation();
  const usersTemplate = useMemo(() => createUsersTemplate(translate), [translate]);
  const usersSettingsTemplate = useMemo(() => createUserSettingsTemplate(translate), [translate]);
  const usersAuthorizationsTemplate = useMemo(() => createUserAuthorizationsTemplate(translate), [translate]);
  const usersPushTokensTemplate = useMemo(() => createUserPushTokensTemplate(translate), [translate]);
  const usersPinNumbersTemplate = useMemo(() => createUserPinNumbersTemplate(translate), [translate]);
  const userDefinedAlarmsTemplate = useMemo(() => createUserDefinedAlarmsTemplate(translate), [translate]);
  const userDevicesTemplate = useMemo(() => createUserDevicesTemplate(translate), [translate]);
  const userAlarmsSyncConfig = useMemo(() => createUserAlarmsSyncConfig(translate), [translate]);

  const extendedDeviceAlarmsTemplate = useMemo(() => {
    return createExtendedDeviceAlarmsTemplate(translate);
  }, [translate]);

  const extendedAlarmsTemplate = useMemo(() => {
    return createExtendedAlarmsTemplate(translate, extendedDeviceAlarmsTemplate);
  }, [extendedDeviceAlarmsTemplate, translate]);

  const userMedicationsTemplate = useMemo(() => {
    return createUserMedicationsTemplate(translate, extendedAlarmsTemplate as any);
  }, [extendedAlarmsTemplate, translate]);

  // Services
  const subscriptionsTemplate = useMemo(() => createSubscriptionsTemplate(translate), [translate]);
  const subscriptionsDetailUsersTemplate = useMemo(
    () => createSubscriptionsDetailUsersTemplate(translate),
    [translate]
  );
  const subscriptionsDetailPharmaciesTemplate = useMemo(
    () => createSubscriptionsDetailPharmaciesTemplate(translate),
    [translate]
  );

  const subscriptionsPharmaciesTemplate = useMemo(() => createSubscriptionsPharmaciesTemplate(translate), [translate]);
  const pharmacyPlanTypesTemplate = useMemo(() => createPharmacyPlanTypesTemplate(translate), [translate]);
  const pharmacyLedgerTemplate = useMemo(() => createPharmacyLedgerTemplate(translate), [translate]);
  const pharmacyCampaignsTemplate = useMemo(() => createPharmacyCampaignsTemplate(translate), [translate]);
  const pharmaciesConsentsTemplate = useMemo(() => createPharmaciesConsentsTemplate(translate), [translate]);
  const pharmaciesIntegrationsTemplate = useMemo(() => createPharmaciesIntegrationsTemplate(translate), [translate]);
  const pharmaciesUsersTemplate = useMemo(() => createPharmaciesSharedAccessesTemplate(translate), [translate]);
  const pharmaciesUserStatsTemplate = useMemo(() => createPharmaciesUserStatsTemplate(translate), [translate]);
  const subscriptionsDetailPharmaciesConsentsTemplate = useMemo(
    () => createSubscriptionsDetailPharmaciesConsentsTemplate(translate),
    [translate]
  );

  const subscriptionsDetailPharmaciesIntegrationsTemplate = useMemo(() => {
    return createSubscriptionsDetailPharmaciesIntegrationsTemplate(translate);
  }, [translate]);

  const serviceOrdersTemplate = useMemo(() => createOrdersTemplate(translate), [translate]);
  const serviceSalesTemplate = useMemo(() => createSalesTemplate(translate), [translate]);

  // Medical instruments
  const drugsTemplate = useMemo(() => createDrugsTemplate(translate), [translate]);
  const missingDrugsTemplate = useMemo(() => createMissingDrugsTemplate(translate), [translate]);
  const ccisCodesTemplate = useMemo(() => createCCIsCodesTemplate(translate), [translate]);
  const ccisDrugsTemplate = useMemo(() => createCCIsDrugsTemplate(translate), [translate]);
  const ccisTemplate = useMemo(() => createCCIsTemplate(translate), [translate]);

  // Tables
  const dialCodesTemplate = useMemo(() => createDialCodesTemplate(translate), [translate]);
  const devicesTemplate = useMemo(() => createDevicesTemplate(translate), [translate]);

  // Support
  const supportOpenTicketsTemplate = useMemo(() => createSupportOpenTicketsTemplate(translate), [translate]);

  // Settings
  const languagesTemplate = useMemo(() => createLanguagesTemplate(translate), [translate]);
  const configTemplate = useMemo(() => createConfigTemplate(translate), [translate]);

  // Developer
  const logsTemplate = useMemo(() => createLogsTemplate(translate), [translate]);
  const logsTherapyTemplate = useMemo(() => createLogsTherapyTemplate(translate), [translate]);
  const ignoreUrlLogsTemplate = useMemo(() => createIgnoreUrlLogsTemplate(translate), [translate]);
  const translationsTemplate = useMemo(() => createTranslationsTemplate(translate), [translate]);
  const missingTranslationsTemplate = useMemo(() => createMissingTranslationsTemplate(translate), [translate]);

  return (
    <Routes>
      <Route Component={() => <Home />} path={appRoutes.home} />

      <Route Component={() => <DashboardHeader />} path={appRoutes.dashboard.base}>
        <Route Component={() => <SkinDashboard />} path={appRoutes.dashboard.home.base}>
          <Route Component={() => <DashboardOverview />} index />
        </Route>

        <Route Component={() => <SkinUsers />} path={appRoutes.dashboard.users.base}>
          <Route Component={() => <UsersOverview />} index />
          <Route Component={() => <UsersDetailOverview />} path={appRoutes.dashboard.users.detail.base} />
          <Route Component={() => <BiometricData />} path={appRoutes.dashboard.users.detail.biometricData} />
          <Route Component={() => <UsersMetrics />} path={appRoutes.dashboard.users.metrics} />

          {listCrudRoutes(usersTemplate)}
          {listCrudRoutes(usersSettingsTemplate)}
          {listCrudRoutes(usersAuthorizationsTemplate)}
          {listCrudRoutes(usersPushTokensTemplate)}
          {listCrudRoutes(usersPinNumbersTemplate)}
          {listCrudRoutes(userDefinedAlarmsTemplate)}
          {listCrudRoutes(userDevicesTemplate)}
          {listCrudRoutes(userMedicationsTemplate)}

          <Route Component={() => <SalesList />} path={appRoutes.dashboard.users.detail.sales} />
          <Route Component={() => <SaleCreate />} path={appRoutes.dashboard.users.detail.salesAdd} />
          <Route Component={() => <SaleEdit />} path={appRoutes.dashboard.users.detail.saleEdit} />
          <Route Component={() => <SaleDetail />} path={appRoutes.dashboard.users.detail.salesDetail} />

          {listCrudRoutes(userAlarmsSyncConfig)}
        </Route>

        <Route Component={() => <SkinServices />} path={appRoutes.dashboard.services.subscriptionsList}>
          {listCrudRoutes(subscriptionsTemplate)}
          {listCrudRoutes(subscriptionsDetailUsersTemplate)}
          {listCrudRoutes(subscriptionsPharmaciesTemplate)}
          {listCrudRoutes(pharmacyPlanTypesTemplate)}
          {listCrudRoutes(pharmacyLedgerTemplate)}
          {listCrudRoutes(pharmacyCampaignsTemplate)}
          <Route Component={() => <PharmacyPlan />} path={appRoutes.dashboard.services.pharmacies.plan} />
          {listCrudRoutes(pharmaciesUserStatsTemplate)}
          {listCrudRoutes(pharmaciesConsentsTemplate)}
          {listCrudRoutes(pharmaciesIntegrationsTemplate)}
          {listCrudRoutes(pharmaciesUsersTemplate)}
          {listCrudRoutes(subscriptionsDetailPharmaciesTemplate)}
          {listCrudRoutes(subscriptionsDetailPharmaciesConsentsTemplate)}
          {listCrudRoutes(subscriptionsDetailPharmaciesIntegrationsTemplate)}
          {listCrudRoutes(serviceOrdersTemplate)}
          {listCrudRoutes(serviceSalesTemplate)}

          <Route Component={() => <OrderDetail />} path={appRoutes.dashboard.services.orderDetail} />
          <Route Component={() => <SaleDetail />} path={appRoutes.dashboard.services.salesDetail} />
        </Route>

        <Route Component={() => <SkinMedicalInstruments />} path={appRoutes.dashboard.medical.base}>
          <Route Component={() => <MedicalInstrumentsOverview />} index />
          <Route Component={() => <MissingDrugDetail />} path={appRoutes.dashboard.medical.missingDrugsDetail} />
          <Route Component={() => <ConvertMissingDrug />} path={appRoutes.dashboard.medical.missingDrugsConvert} />

          {listCrudRoutes(drugsTemplate)}
          {listCrudRoutes(missingDrugsTemplate)}
          {listCrudRoutes(ccisCodesTemplate)}
          {listCrudRoutes(ccisDrugsTemplate)}
          {listCrudRoutes(ccisTemplate)}
        </Route>

        <Route Component={() => <SkinTables />} path={appRoutes.dashboard.tables.base}>
          <Route Component={() => <TablesOverview />} index />

          {listCrudRoutes(dialCodesTemplate)}
          {listCrudRoutes(devicesTemplate)}
        </Route>

        <Route Component={() => <SkinSupport />} path={appRoutes.dashboard.support.base}>
          <Route Component={() => <SupportOverview />} index />
          <Route Component={() => <TicketDetail />} path={appRoutes.dashboard.support.openTicketById} />

          {listCrudRoutes(supportOpenTicketsTemplate)}
        </Route>

        <Route Component={() => <SkinSettings />} path={appRoutes.dashboard.settings.base}>
          <Route Component={() => <SettingsOverview />} index />
          <Route Component={() => <SettingsProfile />} path={appRoutes.dashboard.settings.profile} />

          {listCrudRoutes(languagesTemplate)}
          {listCrudRoutes(configTemplate)}
        </Route>

        <Route Component={() => <SkinDeveloper />} path={appRoutes.dashboard.developer.base}>
          <Route Component={() => <DeveloperOverview />} index />
          <Route Component={() => <SmsTest />} path={appRoutes.dashboard.developer.smsTest} />

          {listCrudRoutes(logsTemplate)}
          {listCrudRoutes(logsTherapyTemplate)}
          {listCrudRoutes(ignoreUrlLogsTemplate)}
          {listCrudRoutes(translationsTemplate)}
          {listCrudRoutes(missingTranslationsTemplate)}
        </Route>
      </Route>

      <Route Component={() => <Navigate to={appRoutes.home} />} path={'*'} />
    </Routes>
  );
}
